/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { Subject, combineLatest, filter, map, takeUntil, withLatestFrom } from 'rxjs';

import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { Camera } from '@appCanvasCommon/core/Camera';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { RendererCache } from '@appPlanApplication/ticket-renderer';

import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';

import { InsertionPointRenderer } from './InsertionPointRenderer';
import { InsertionPointContainer, InsertionPointContainerConfiguration } from './InsertionPointContainer';
import { snapping } from 'utils/ticket-utils';

/**
 * The stickiness value used for the snapping behavior.
 */
const DEFAULT_SNAP_STICKINESS = 15;

/**
 * This class is responsible for creating the Insertion Point canvas object by invoking the InsertionPointContainer.
 * It facilitates communication between the canvas element and the importTicketsCommonService to emit and listen for changes.
 * Additionally, it sets the initial value of the insertion point as soon as the element becomes visible on the screen.
 */
export class InsertionPoint {
	private _destroySubject$: Subject<void> = new Subject();
	private _insertionPointTextureRendererCache: RendererCache;
	private _insertionPointContainer: InsertionPointContainer;

	constructor(
		private canvasAngularLand: CanvasAngularLand,
		private parentLayer: FancyContainer,
		private camera: Camera,
		private app: AbstractApplication
	) {
		this.init();
	}

	private init(): void {
		this._insertionPointTextureRendererCache = new InsertionPointRenderer();
		this._insertionPointContainer = new InsertionPointContainer({
			visible: false,
			width: this.camera.view.width,
			y: 0,
		});
		this.parentLayer.addChild(this._insertionPointContainer);
		this.listenInsertionChanges();
		this.listenDragEvent();
		this.setInitialYvalue();
	}

	private listenDragEvent(): void {
		combineLatest([
			this._insertionPointContainer.onDrag$,
			this.canvasAngularLand.importTicketsCommonService.swimlanesPositions$,
		])
			.pipe(
				withLatestFrom(this.canvasAngularLand.importTicketsCommonService.isInsertionPointVisible$),
				filter(([_, isVisible]) => !!isVisible),
				map(([[screenYPosition, swimlanesPositions]]) =>
					this.getSwinlaneSnapPositionIfAny(screenYPosition, swimlanesPositions)
				),
				map((screenYPosition) => this.camera.screenToRealY(screenYPosition)),
				takeUntil(this._destroySubject$)
			)
			.subscribe((realYPosition) => {
				this.canvasAngularLand.importTicketsCommonService.setInsertionPointY(realYPosition);
			});
	}

	private listenInsertionChanges(): void {
		combineLatest([
			this.camera.changed$,
			this.canvasAngularLand.importTicketsCommonService.isInsertionPointVisible$,
			this.canvasAngularLand.importTicketsCommonService.insertionPointY$.pipe(
				filter((insertionPointY) => insertionPointY !== null)
			),
		])
			.pipe(takeUntil(this._destroySubject$))
			.subscribe(([_, isVisible, insertionPointY]) => {
				const insertionPointContainerUpdatedProperties: InsertionPointContainerConfiguration = {
					visible: isVisible,
					width: this.camera.view.width,
					y: this.camera.realToScreenY(insertionPointY),
				};
				this._insertionPointContainer.update(insertionPointContainerUpdatedProperties);
				this.app.markAsDirty();
			});
	}

	private setInitialYvalue(): void {
		// This method allows setting the initial Y position of the insertion point to the middle of the screen when insertionPointY is null.
		this.canvasAngularLand.importTicketsCommonService.isInsertionPointVisible$
			.pipe(
				withLatestFrom(this.canvasAngularLand.importTicketsCommonService.insertionPointY$),
				filter(([isVisible, insertionPointY]) => !!isVisible && insertionPointY === null),
				takeUntil(this._destroySubject$)
			)
			.subscribe(() => {
				this.canvasAngularLand.importTicketsCommonService.setInsertionPointY(
					this.camera.screenToRealY(this.camera.view.height / 2)
				);
			});
	}

	private getSwinlaneSnapPositionIfAny(screenYPosition: number, swimlanesPositions: number[]): number {
		// Define the comp callback function
		const comp = function (position: number) {
			return position;
		};

		const stickiness = window.swimlaneSnapStickiness || DEFAULT_SNAP_STICKINESS;

		// Use the snapping function to find the closest y-axis value of the nearest swimlane edge
		const closestSwimlaneEdge = snapping(screenYPosition, swimlanesPositions, stickiness, comp);

		// Return the y-axis value of the point to snap to the closest edge of the nearest swimlane
		return closestSwimlaneEdge ? closestSwimlaneEdge.loc : Math.round(screenYPosition);
	}

	public destroy(): void {
		this._insertionPointContainer.destroy();
		this._insertionPointTextureRendererCache.destroy();
		this._destroySubject$.next();
		this._destroySubject$.complete();
	}
}
